/**
 * DI-2-2 §2.2 — provisional flights inside one position cluster.
 *
 *   1. set aside the GROUND group: rows with `altRel < GROUND_CUT_M` (DI-D17
 *      c1/c4 — the legacy `altitude_relative < 25 → INACTIVE`, here a group,
 *      not a status). Kept and shown, never discarded (DI-D10). A row without
 *      `altRel` is not known to be on the ground and stays in the flights.
 *   2. set aside rows without a capture time (`noTime`) — they cannot be placed.
 *   3. sort the rest by `capturedAtMs` (then row index — never the file name).
 *   4. split where the gap to the previous image is `>= FLIGHT_MERGE_S`.
 *
 * Every group is `provisional: true` — the flight logs decide (DI-2-7). A
 * browser group is never a flight row.
 */
import { cadence } from "./cadence.js";
import { byTimeThenIndex } from "./cluster.js";
import type { ProvisionalFlight } from "./types.js";

/** DI-D17 c1/c4: the 25 m ground cut, kept from the legacy pipeline. */
export const GROUND_CUT_M = 25;

/**
 * DI-D39 c4 (Christian 2026-09-25, story DI-2-13 — supersedes DI-D17 c1's
 * "< 2 min merge"): "Images only (no log): split at gaps > 3 min …; the
 * browser's provisional groups use the same 3 min." A rotor change grounds
 * the drone for at least three minutes, so a gap of 180 s or more starts a
 * new provisional group. With a log the flight logs decide (the log's
 * inspection runs, never a gap — DI-D39 c1/c2); these groups stay
 * provisional. The worker's twin for the log-less route is
 * `_BACKEND` `IMAGES_ONLY_FLIGHT_GAP_S` (DI-4-5 uses it).
 *
 * F-M400 has one 120.021 s gap (`_00069` → `_00070`): under 180 s it is ONE
 * provisional flight now; its log makes it two (DI-2-13).
 */
export const FLIGHT_MERGE_S = 180;

export interface FlightRow {
  index: number;
  t?: number | undefined;
  altRel?: number | undefined;
}

export interface FlightSplit {
  ground: number[];
  noTime: number[];
  flights: ProvisionalFlight[];
}

export function isGroundShot(altRel: number | undefined): boolean {
  return altRel !== undefined && altRel < GROUND_CUT_M;
}

export function splitFlights(
  clusterId: string,
  rows: readonly FlightRow[],
  mergeS: number = FLIGHT_MERGE_S,
): FlightSplit {
  const ground: number[] = [];
  const noTime: number[] = [];
  const timed: { index: number; t: number }[] = [];
  for (const r of rows.slice().sort(byTimeThenIndex)) {
    if (isGroundShot(r.altRel)) ground.push(r.index);
    else if (r.t === undefined) noTime.push(r.index);
    else timed.push({ index: r.index, t: r.t });
  }

  const groups: { index: number; t: number }[][] = [];
  const gaps: (number | null)[] = [];
  let current: { index: number; t: number }[] = [];
  const splitMs = mergeS * 1000;
  for (const f of timed) {
    const prev = current[current.length - 1];
    if (prev && f.t - prev.t >= splitMs) {
      groups.push(current);
      gaps.push((f.t - prev.t) / 1000);
      current = [];
    }
    current.push(f);
  }
  if (current.length > 0) groups.push(current);

  const flights = groups.map(
    (g, k): ProvisionalFlight => ({
      flightId: `${clusterId}-f${String(k + 1)}`,
      start: (g[0] as { t: number }).t,
      end: (g[g.length - 1] as { t: number }).t,
      imageIndexes: g.map((f) => f.index),
      gapBeforeS: k === 0 ? null : (gaps[k - 1] ?? null),
      provisional: true,
      cadence: cadence(g),
    }),
  );
  return { ground, noTime, flights };
}
